import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const JobMonitorPanel = ({ className = '' }) => {
  const [jobs, setJobs] = useState([
    {
      id: 'job_1730905226159',
      type: 'bulk_upload',
      fileName: 'crane_inventory_q4_2024.xlsx',
      status: 'completed',
      progress: 100,
      startTime: new Date(Date.now() - 3600000),
      endTime: new Date(Date.now() - 3300000),
      totalItems: 45,
      processedItems: 42,
      errorCount: 3,
      successCount: 42
    },
    {
      id: 'job_1730901626159',
      type: 'bulk_revaluation',
      fileName: 'Selected 15 items',
      status: 'processing',
      progress: 67,
      startTime: new Date(Date.now() - 900000),
      endTime: null,
      totalItems: 15,
      processedItems: 10,
      errorCount: 0,
      successCount: 10
    },
    {
      id: 'job_1730898026159',
      type: 'export',
      fileName: 'inventory_export_2024_11_06.csv',
      status: 'completed',
      progress: 100,
      startTime: new Date(Date.now() - 7200000),
      endTime: new Date(Date.now() - 7020000),
      totalItems: 128,
      processedItems: 128,
      errorCount: 0,
      successCount: 128
    },
    {
      id: 'job_1730894426159',
      type: 'bulk_upload',
      fileName: 'new_acquisitions_oct_2024.csv',
      status: 'failed',
      progress: 25,
      startTime: new Date(Date.now() - 10800000),
      endTime: new Date(Date.now() - 10500000),
      totalItems: 23,
      processedItems: 6,
      errorCount: 17,
      successCount: 6
    }
  ]);

  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setJobs(prevJobs => prevJobs?.map(job => {
        if (job?.status === 'processing' && job?.progress < 100) {
          const newProgress = Math.min(100, job?.progress + Math.random() * 10);
          const newProcessedItems = Math.floor((newProgress / 100) * job?.totalItems);
          
          if (newProgress >= 100) {
            return {
              ...job,
              status: 'completed',
              progress: 100,
              endTime: new Date(),
              processedItems: job?.totalItems,
              successCount: job?.totalItems - job?.errorCount
            };
          }
          
          return {
            ...job,
            progress: newProgress,
            processedItems: newProcessedItems,
            successCount: newProcessedItems - job?.errorCount
          };
        }
        return job;
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const getJobTypeIcon = (type) => {
    switch (type) {
      case 'bulk_upload': return 'Upload';
      case 'bulk_revaluation': return 'RefreshCw';
      case 'export': return 'Download';
      default: return 'FileText';
    }
  };

  const getJobTypeLabel = (type) => {
    switch (type) {
      case 'bulk_upload': return 'Bulk Upload';
      case 'bulk_revaluation': return 'Bulk Revaluation';
      case 'export': return 'Export';
      default: return 'Job';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'processing': return 'text-primary';
      case 'failed': return 'text-error';
      case 'queued': return 'text-warning';
      default: return 'text-text-secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'processing': return 'Loader2';
      case 'failed': return 'XCircle';
      case 'queued': return 'Clock';
      default: return 'Circle';
    }
  };

  const formatDuration = (startTime, endTime) => {
    const duration = (endTime || new Date()) - startTime;
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const formatTime = (date) => {
    return new Date(date)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const downloadJobReport = (jobId) => {
    // Mock job report download
    const reportContent = `Job Report - ${jobId}\nGenerated: ${new Date()?.toISOString()}\n\nJob completed successfully.`;
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job_report_${jobId}.txt`;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const retryJob = (jobId) => {
    setJobs(prevJobs => prevJobs?.map(job => 
      job?.id === jobId 
        ? { ...job, status: 'processing', progress: 0, startTime: new Date(), endTime: null }
        : job
    ));
  };

  const cancelJob = (jobId) => {
    setJobs(prevJobs => prevJobs?.map(job => 
      job?.id === jobId && job?.status === 'processing'
        ? { ...job, status: 'failed', endTime: new Date() }
        : job
    ));
  };

  return (
    <div className={`bg-surface border border-border rounded-lg shadow-card ${className}`}>
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">Job Monitor</h3>
          <div className="flex items-center space-x-3">
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e?.target?.checked)}
                className="rounded border-border"
              />
              <span className="text-text-secondary">Auto-refresh</span>
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location?.reload()}
              iconName="RefreshCw"
            />
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {jobs?.map((job) => (
            <div key={job?.id} className="bg-muted p-4 rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={getJobTypeIcon(job?.type)} 
                    size={20} 
                    className="text-text-secondary" 
                  />
                  <div>
                    <h4 className="font-medium text-text-primary">
                      {getJobTypeLabel(job?.type)}
                    </h4>
                    <p className="text-sm text-text-secondary">{job?.fileName}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={getStatusIcon(job?.status)} 
                    size={16} 
                    className={`${getStatusColor(job?.status)} ${job?.status === 'processing' ? 'animate-spin' : ''}`}
                  />
                  <span className={`text-sm capitalize ${getStatusColor(job?.status)}`}>
                    {job?.status}
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              {job?.status === 'processing' && (
                <div className="mb-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-text-secondary">Progress</span>
                    <span className="text-text-primary">{Math.round(job?.progress)}%</span>
                  </div>
                  <div className="w-full bg-border rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${job?.progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Job Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3 text-sm">
                <div>
                  <span className="text-text-secondary">Total:</span>
                  <div className="font-medium text-text-primary">{job?.totalItems}</div>
                </div>
                <div>
                  <span className="text-text-secondary">Processed:</span>
                  <div className="font-medium text-text-primary">{job?.processedItems}</div>
                </div>
                <div>
                  <span className="text-text-secondary">Success:</span>
                  <div className="font-medium text-success">{job?.successCount}</div>
                </div>
                <div>
                  <span className="text-text-secondary">Errors:</span>
                  <div className="font-medium text-error">{job?.errorCount}</div>
                </div>
              </div>

              {/* Job Timing */}
              <div className="flex items-center justify-between text-sm text-text-secondary mb-3">
                <div>
                  <span>Started: {formatTime(job?.startTime)}</span>
                  {job?.endTime && (
                    <span className="ml-4">
                      Duration: {formatDuration(job?.startTime, job?.endTime)}
                    </span>
                  )}
                </div>
                <div className="font-mono text-xs">
                  {job?.id}
                </div>
              </div>

              {/* Job Actions */}
              <div className="flex items-center justify-end space-x-2">
                {job?.status === 'completed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadJobReport(job?.id)}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Report
                  </Button>
                )}
                
                {job?.status === 'failed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => retryJob(job?.id)}
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Retry
                  </Button>
                )}
                
                {job?.status === 'processing' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => cancelJob(job?.id)}
                    iconName="X"
                    iconPosition="left"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {jobs?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Activity" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">No recent jobs</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobMonitorPanel;